/**
 * C7-3 (SI-8) → HB-5: the explicit selection→request adapter for the operator
 * override, now in the CREATE WIZARD's vocabulary.
 *
 * The dialog no longer owns a `CompositeDataPicker`, so there is no
 * `HydratedData` tree to flatten. What the operator edits is two lists —
 * insight theses and social-proof rows — and they travel on two channels the
 * server already speaks:
 *
 *  - `sourceDataOverride` (`HydratedSourceData`): the head's OWN source data
 *    with the social-proof collections narrowed to the operator's tick. The
 *    resolver replaces `sourceData` wholesale with this object, so audiences,
 *    tasks and the hydrated analyses ride along UNCHANGED — an override that
 *    carried only the four social-proof collections would silently strip the
 *    audiences the run is grounded in.
 *  - `insightSelection` (`{ insightIds }`): the thesis narrowing. Theses do not
 *    live in `sourceData` at all — the resolver loads them from the pinned
 *    analysis and narrows them with the head's frozen manifest — so the thesis
 *    tick has to reach the SAME loader, as an operator-side selection that
 *    outranks the frozen one for this run only (W1.4).
 *
 * `variables` are still NOT part of the override: strategy variables travel on
 * their own request channel (`strategyVariables`), never inside sourceData —
 * which is exactly why the dialog shows them read-only.
 */
import type { HydratedSourceData } from '@modules/shared-types';

/** The four planes one social-proof table writes. */
export type SocialProofPlane = 'review' | 'rating' | 'studyCase' | 'bigNumber';

export interface SelectedSocialProofEntity {
  id: string;
  plane: SocialProofPlane;
  /** The hydrated entity, exactly as the strategy snapshot carries it. */
  entity: Record<string, unknown>;
}

const PLANE_TO_COLLECTION: Record<SocialProofPlane, keyof HydratedSourceData> = {
  review: 'reviews',
  rating: 'ratings',
  studyCase: 'studyCases',
  bigNumber: 'bigNumbers',
};

const listOf = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

export interface BlockSourceDataOverrideInput {
  /** The head's `sourceData` — the base every override narrows. */
  strategySourceData?: Record<string, unknown>;
  /** Social-proof rows the operator kept ticked, hydrated. */
  socialProof: SelectedSocialProofEntity[];
}

/**
 * `{ socialProofIds }` + the head's source data → the `sourceDataOverride` shape
 * `BlockScreenGenerationContextResolver` accepts (`sourceDataOrigin:
 * 'operator-override'`).
 */
export function toSourceDataOverride(input: BlockSourceDataOverrideInput): HydratedSourceData {
  const base = input.strategySourceData ?? {};
  const collections: Record<string, unknown[]> = {
    reviews: [], ratings: [], studyCases: [], bigNumbers: [],
  };
  for (const row of input.socialProof) {
    collections[PLANE_TO_COLLECTION[row.plane] as string].push(row.entity);
  }

  const override: HydratedSourceData = {
    audiences: listOf(base.audiences) as HydratedSourceData['audiences'],
    tasks: listOf(base.tasks) as HydratedSourceData['tasks'],
    studyCases: collections.studyCases as HydratedSourceData['studyCases'],
    reviews: collections.reviews as HydratedSourceData['reviews'],
    ratings: collections.ratings as unknown as HydratedSourceData['ratings'],
    bigNumbers: collections.bigNumbers as HydratedSourceData['bigNumbers'],
    ...(listOf(base.insights).length > 0
      ? { insights: base.insights as NonNullable<HydratedSourceData['insights']> }
      : {}),
  };
  return override;
}

/**
 * `{ insightSelection }` — the thesis narrowing, in the shape
 * `normalizeInsightSelectionRequest` reads off the request body. Returns
 * `undefined` when there is nothing to narrow, so an empty analysis never sends
 * an "explicit selection of nothing" (which the server refuses by contract).
 */
export function toInsightSelectionOverride(
  thesisIds: string[],
): { insightIds: string[] } | undefined {
  const ids = [...new Set(thesisIds.filter((id) => typeof id === 'string' && id))];
  return ids.length > 0 ? { insightIds: ids } : undefined;
}

/** True when the override would carry at least one entity — an all-empty
 * override must not be sent (the server treats {} as absent anyway). */
export function overrideHasValues(override: HydratedSourceData): boolean {
  return [
    override.audiences, override.tasks, override.studyCases,
    override.reviews, override.ratings, override.bigNumbers,
    override.insights ?? [],
  ].some((collection) => Array.isArray(collection) && collection.length > 0);
}
